<template>
    <div class="space-y-4">
        <p class="text-lg font-extrabold text-white uppercase">{{ $t('profile.play_with_friends') }}</p>

        <div class="space-y-3">
            <div>
                <FriendSearchInput
                    v-model="friendSearchQuery"
                    :placeholder="$t('friends.search_placeholder')"
                    @update:modelValue="debouncedSearch"
                />
            </div>

            <!-- Search Results -->
            <div v-if="friendSearchQuery.trim()" class="space-y-2">
                <div
                    v-if="searchResults.length === 0 && !isSearching"
                    class="pb-4 font-semibold text-white"
                >
                    {{ $t('profile.no_user_found') }}
                </div>

                <div v-if="isSearching" class="text-titles py-4 text-center">
                    {{ $t('profile.searching') }}
                </div>

                <div v-if="searchResults.length > 0" class="space-y-2">
                    <FriendItem
                        v-for="friend in searchResults"
                        :key="friend.id"
                        :friend="friend"
                        :loading="loadingFriendId === friend.id"
                    >
                        <template #button="{ friend: result }">
                            <DiagonalCutButton
                                v-if="searchStatus(result) === 'incoming'"
                                variant="primary"
                                cut-position="left-top"
                                :full-width="false"
                                :loading="loadingFriendId === result.id"
                                :disabled="loadingFriendId === result.id"
                                data-testid="friend-accept-button"
                                @click="acceptFromSearch(result)"
                            >
                                {{ $t('friends.accept') }}
                            </DiagonalCutButton>
                            <span
                                v-else-if="searchStatus(result) === 'sent'"
                                class="text-titles text-sm font-semibold"
                            >
                                {{ $t('profile.requested') }}
                            </span>
                            <span
                                v-else-if="searchStatus(result) === 'friends'"
                                class="text-titles text-sm font-semibold"
                            >
                                {{ $t('friends.friends_heading') }}
                            </span>
                            <DiagonalCutButton
                                v-else
                                variant="primary"
                                cut-position="left-top"
                                :full-width="false"
                                :loading="loadingFriendId === result.id"
                                :disabled="loadingFriendId === result.id"
                                data-testid="friend-add-button"
                                @click="handleFriendAction(result)"
                            >
                                {{ $t('profile.friend_button_add') }}
                            </DiagonalCutButton>
                        </template>
                    </FriendItem>
                </div>
            </div>

            <!-- Received Requests -->
            <div v-if="!friendSearchQuery.trim() && receivedRequests.length > 0">
                <div class="mb-2 flex items-center justify-between">
                    <button
                        @click="toggleReceivedRequestsSection"
                        class="flex items-center space-x-2 font-bold text-white"
                    >
                        <span>{{ $t('profile.friend_requests_heading') }}</span>
                        <svg
                            :class="['size-4 transition-transform', showReceivedRequestsSection ? 'rotate-180' : '']"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                        >
                            <path
                                fill-rule="evenodd"
                                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                clip-rule="evenodd"
                            />
                        </svg>
                    </button>
                    <span class="font-bold text-white">{{ receivedRequests.length }}</span>
                </div>

                <div v-if="showReceivedRequestsSection" class="space-y-2">
                    <FriendItem
                        v-for="request in receivedRequests"
                        :key="request.id"
                        :friend="request.friend"
                        :show-button="true"
                        :buttons-new-line="true"
                    >
                        <template #button>
                            <div class="flex gap-2">
                                <DiagonalCutButton
                                    variant="secondary"
                                    cut-position="right-top"
                                    :full-width="true"
                                    :disabled="actioningRequestId === request.id"
                                    data-testid="friend-decline-button"
                                    @click="declineReceivedRequest(request)"
                                >
                                    {{ $t('friends.decline') }}
                                </DiagonalCutButton>
                                <DiagonalCutButton
                                    variant="secondary"
                                    cut-position="both-top"
                                    :full-width="true"
                                    :disabled="actioningRequestId === request.id"
                                    data-testid="friend-block-button"
                                    @click="blockReceivedRequest(request)"
                                >
                                    {{ $t('friends.block') }}
                                </DiagonalCutButton>
                                <DiagonalCutButton
                                    variant="primary"
                                    cut-position="left-top"
                                    :full-width="true"
                                    :loading="actioningRequestId === request.id"
                                    :disabled="actioningRequestId === request.id"
                                    data-testid="friend-accept-request-button"
                                    @click="acceptReceivedRequest(request)"
                                >
                                    {{ $t('friends.accept') }}
                                </DiagonalCutButton>
                            </div>
                        </template>
                    </FriendItem>
                </div>
            </div>

            <!-- Sent Requests -->
            <div v-if="!friendSearchQuery.trim() && sentRequests.length > 0">
                <div class="mb-2 flex items-center justify-between">
                    <button
                        @click="toggleSentRequestsSection"
                        class="flex items-center space-x-2 font-bold text-white"
                    >
                        <span>{{ $t('profile.sent_requests') }}</span>
                        <svg
                            :class="['size-4 transition-transform', showSentRequestsSection ? 'rotate-180' : '']"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                        >
                            <path
                                fill-rule="evenodd"
                                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                clip-rule="evenodd"
                            />
                        </svg>
                    </button>
                    <span class="font-bold text-white">{{ sentRequests.length }}</span>
                </div>

                <div v-if="showSentRequestsSection" class="space-y-2">
                    <FriendItem
                        v-for="request in sentRequests"
                        :key="request.id"
                        :friend="request.friend"
                        :show-button="true"
                        :button-text="$t('profile.friend_button_cancel')"
                        :loading="cancelingRequestId === request.id"
                        @action="() => cancelFriendRequest(request)"
                    />
                </div>
            </div>

            <!-- Online Friends -->
            <AccordionSection
                v-if="!friendSearchQuery.trim() && onlineFriends.length > 0"
                :title="$t('profile.online')"
                :count="onlineFriends.length"
                v-model="showOnlineSection"
            >
                <FriendItem
                    v-for="friendship in onlineFriends"
                    :key="friendship.id"
                    :friend="friendship.friend"
                    :show-button="true"
                    :button-text="$t('friends.remove')"
                    :loading="removingFriendId === friendship.id"
                    :show-online-indicator="true"
                    :is-online="true"
                    @action="() => removeFriend(friendship)"
                />
            </AccordionSection>

            <!-- Offline Friends -->
            <AccordionSection
                v-if="!friendSearchQuery.trim() && offlineFriends.length > 0"
                :title="$t('profile.offline')"
                :count="offlineFriends.length"
                v-model="showOfflineSection"
            >
                <FriendItem
                    v-for="friendship in offlineFriends"
                    :key="friendship.id"
                    :friend="friendship.friend"
                    :show-button="true"
                    :button-text="$t('friends.remove')"
                    :loading="removingFriendId === friendship.id"
                    :is-online="false"
                    @action="() => removeFriend(friendship)"
                />
            </AccordionSection>
        </div>
    </div>

    <ShareQr :url="shareUrl" v-model="showQr" />

    <Modal
        :is-visible="!!pendingRemovalFriendship"
        :on-confirm="confirmRemoveFriend"
        :on-decline="cancelRemoveFriend"
        :is-loading="!!removingFriendId"
    >
        <h2 class="justify-start text-center font-['Termina'] text-base leading-normal font-extrabold text-white">
            {{ $t('profile.remove_friend_title') }}
        </h2>
        <p class="mt-4 justify-start self-stretch text-center font-sans text-sm leading-normal font-normal text-white">
            {{
                $t('profile.remove_friend_body', {
                    name:
                        pendingRemovalFriendship?.friend?.tag_name ||
                        pendingRemovalFriendship?.friend?.name ||
                        $t('friends.this_friend'),
                })
            }}
        </p>
    </Modal>
</template>

<script>
import { mapStores } from 'pinia'
import { useAuthStore } from '@/stores/auth.js'
import { useFriends } from '@/composables/useFriends.js'
import FriendItem from '@/components/ui/FriendItem.vue'
import FriendSearchInput from '@/components/ui/FriendSearchInput.vue'
import DiagonalCutButton from '@/components/ui/DiagonalCutButton.vue'
import ShareQr from '@/components/ui/ShareQr.vue'
import AccordionSection from '@/components/ui/AccordionSection.vue'
import Modal from '@/components/Modal.vue'
import { isOnline } from '@/utils/presence.js'

const PRESENCE_REFRESH_MS = 60 * 1000

export default {
    name: 'ProfileFriendsTab',
    components: {
        FriendItem,
        FriendSearchInput,
        DiagonalCutButton,
        ShareQr,
        AccordionSection,
        Modal,
    },
    setup() {
        const friendsComposable = useFriends()
        const authStore = useAuthStore()
        return {
            ...friendsComposable,
            authStore,
        }
    },
    data() {
        return {
            showReceivedRequestsSection: true,
            showSentRequestsSection: true,
            actioningRequestId: null,
            showOnlineSection: true,
            showOfflineSection: true,
            showQr: false,
            removingFriendId: null,
            pendingRemovalFriendship: null,
            now: Date.now(),
            presenceTimer: null,
        }
    },
    computed: {
        ...mapStores(useAuthStore),
        sentRequests() {
            return this.authStore.sent_requests || []
        },
        receivedRequests() {
            return this.authStore.received_requests || []
        },
        onlineFriends() {
            return (this.authStore.friends || []).filter((friendship) =>
                isOnline(friendship.friend.last_seen_at, this.now),
            )
        },
        offlineFriends() {
            return (this.authStore.friends || []).filter(
                (friendship) => !isOnline(friendship.friend.last_seen_at, this.now),
            )
        },
        shareUrl() {
            return `https://dartshopper.com/`
        },
    },
    mounted() {
        // We automatically start presence polling since the user routed here
        this.startPresencePolling()
    },
    beforeUnmount() {
        // Clean up the presence polling
        this.stopPresencePolling()
    },
    methods: {
        sendGameInvite() {
            console.log('Send game invite functionality to be implemented')
        },
        scanQRCode() {
            this.showQr = true
        },
        startPresencePolling() {
            if (this.presenceTimer) return

            this.now = Date.now()
            this.authStore.refreshFriends().catch((error) => {
                console.error('Failed to refresh friends:', error)
            })

            this.presenceTimer = setInterval(() => {
                this.now = Date.now()
                this.authStore.refreshFriends().catch((error) => {
                    console.error('Failed to refresh friends:', error)
                })
            }, PRESENCE_REFRESH_MS)
        },
        stopPresencePolling() {
            if (this.presenceTimer) {
                clearInterval(this.presenceTimer)
                this.presenceTimer = null
            }
        },
        toggleReceivedRequestsSection() {
            this.showReceivedRequestsSection = !this.showReceivedRequestsSection
        },
        toggleSentRequestsSection() {
            this.showSentRequestsSection = !this.showSentRequestsSection
        },
        searchStatus(friend) {
            const friendship = friend.friendship
            if (!friendship) return 'none'
            if (friendship.status === 'accepted') return 'friends'
            return friendship.is_sender ? 'incoming' : 'sent'
        },
        async acceptFromSearch(friend) {
            const friendshipId = friend.friendship?.id
            if (!friendshipId || this.loadingFriendId) return

            this.loadingFriendId = friend.id
            try {
                await this.authStore.acceptFriendRequest(friendshipId)
                this.clearSearch()
            } catch (error) {
                this.$toast(
                    this.$t('general.something_went_wrong'),
                    5000,
                    error.data?.message || this.$t('friends.error_accept_request'),
                )
            } finally {
                this.loadingFriendId = null
            }
        },
        async acceptReceivedRequest(request) {
            if (this.actioningRequestId) return
            this.actioningRequestId = request.id
            try {
                await this.authStore.acceptFriendRequest(request.id)
            } catch (error) {
                this.$toast(
                    this.$t('general.something_went_wrong'),
                    5000,
                    error.data?.message || this.$t('friends.error_accept_request'),
                )
            } finally {
                this.actioningRequestId = null
            }
        },
        async declineReceivedRequest(request) {
            if (this.actioningRequestId) return
            this.actioningRequestId = request.id
            try {
                await this.authStore.declineFriendRequest(request.id)
            } catch (error) {
                this.$toast(
                    this.$t('general.something_went_wrong'),
                    5000,
                    error.data?.message || this.$t('friends.error_decline_request'),
                )
            } finally {
                this.actioningRequestId = null
            }
        },
        async blockReceivedRequest(request) {
            if (this.actioningRequestId) return

            const profileId = request.friend?.id
            if (!profileId) {
                this.$toast(this.$t('general.something_went_wrong'), 5000, this.$t('friends.error_identify_block'))
                return
            }

            this.actioningRequestId = request.id
            try {
                await this.authStore.blockProfile(profileId)
                this.authStore.received_requests = this.authStore.received_requests.filter(
                    (r) => r.id !== request.id,
                )
                this.authStore.declineFriendRequest(request.id).catch(console.error)

                this.$toast(
                    this.$t('friends.user_blocked_title'),
                    3000,
                    this.$t('friends.user_blocked_body', {
                        name: request.friend.tag_name || this.$t('friends.this_user'),
                    }),
                )
            } catch (error) {
                this.$toast(
                    this.$t('general.something_went_wrong'),
                    5000,
                    error.data?.message || this.$t('friends.error_block_user'),
                )
            } finally {
                this.actioningRequestId = null
            }
        },
        async handleFriendAction(friend) {
            await this.sendFriendRequest(friend)
            this.clearSearch()
        },
        removeFriend(friendship) {
            if (this.removingFriendId) return
            this.pendingRemovalFriendship = friendship
        },
        cancelRemoveFriend() {
            if (this.removingFriendId) return
            this.pendingRemovalFriendship = null
        },
        async confirmRemoveFriend() {
            const friendship = this.pendingRemovalFriendship
            if (!friendship || this.removingFriendId) return

            this.removingFriendId = friendship.id
            try {
                await this.authStore.removeFriend(friendship.id)
                this.pendingRemovalFriendship = null
            } catch (error) {
                this.$toast(
                    this.$t('general.something_went_wrong'),
                    5000,
                    error.data?.message || this.$t('friends.error_remove_friend'),
                )
            } finally {
                this.removingFriendId = null
            }
        },
        async shareProfile() {
            if (navigator.share) {
                try {
                    await navigator.share({
                        title: this.$t('profile.share_title'),
                        url: this.shareUrl,
                    })
                } catch (error) {
                    console.error('Failed to share profile:', error)
                }
            } else {
                this.showQr = true
            }
        },
    },
}
</script>